/**
 * CLI Token Modal Component
 * Displays the access token for use with workflow-cli
 * Replaces the need for get-token.html
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';


interface CliTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TokenInfo {
  token: string;
  email?: string;
  name?: string;
  expiresAt: Date;
  isExpired: boolean;
  minutesLeft: number;
}

const CliTokenModal: React.FC<CliTokenModalProps> = ({ isOpen, onClose }) => {
  const { accessToken } = useAuth();
  const { instance, accounts } = useMsal();
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [copyStatus, setCopyStatus] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (isOpen && accessToken) {
      extractTokenInfo(accessToken);
    }
  }, [isOpen, accessToken]);

  const extractTokenInfo = (token: string) => {
    try {
      console.log('🔍 Extracting token info...');
      console.log('  Token type:', typeof token);
      console.log('  Token length:', token?.length);

      if (!token || typeof token !== 'string') {
        throw new Error('Token is not a valid string');
      }

      const parts = token.split('.');
      console.log('  Token parts:', parts.length);

      if (parts.length !== 3) {
        throw new Error(`Invalid JWT: expected 3 parts, got ${parts.length}`);
      }

      // Decode JWT token to get user info and expiry
      // JWT uses base64url encoding, need to convert to standard base64
      let base64Payload = parts[1];
      console.log('  Base64url payload length:', base64Payload.length);

      // Convert base64url to base64
      base64Payload = base64Payload.replace(/-/g, '+').replace(/_/g, '/');

      // Add padding if needed
      while (base64Payload.length % 4 !== 0) {
        base64Payload += '=';
      }

      console.log('  Base64 payload length (after conversion):', base64Payload.length);

      const payload = JSON.parse(atob(base64Payload));
      const expiresAt = new Date(payload.exp * 1000);
      const now = new Date();
      const isExpired = expiresAt < now;
      const minutesLeft = Math.floor((expiresAt.getTime() - now.getTime()) / 1000 / 60);

      // Debug logging
      console.log('🔍 Token Debug Info:');
      console.log('  Raw exp value:', payload.exp);
      console.log('  Expires at (Date):', expiresAt);
      console.log('  Expires at (ISO):', expiresAt.toISOString());
      console.log('  Current time:', now);
      console.log('  Current time (ISO):', now.toISOString());
      console.log('  Time difference (ms):', expiresAt.getTime() - now.getTime());
      console.log('  Minutes left:', minutesLeft);
      console.log('  Is expired?:', isExpired);
      console.log('  Token issued at (iat):', payload.iat ? new Date(payload.iat * 1000).toISOString() : 'N/A');
      console.log('  Token not before (nbf):', payload.nbf ? new Date(payload.nbf * 1000).toISOString() : 'N/A');

      setTokenInfo({
        token,
        email: payload.email || payload.upn || payload.preferred_username,
        name: payload.name,
        expiresAt,
        isExpired,
        minutesLeft,
      });
    } catch (error) {
      console.error('❌ Failed to decode token:', error);
      console.error('  Error details:', error);
      setTokenInfo({
        token,
        expiresAt: new Date(),
        isExpired: true,
        minutesLeft: 0,
      });
    }
  };

  const copyToken = async () => {
    if (!tokenInfo) return;

    try {
      await navigator.clipboard.writeText(tokenInfo.token);
      setCopyStatus('✅ Copied to clipboard!');
      setTimeout(() => setCopyStatus(''), 3000);
    } catch (error) {
      console.error('Failed to copy token:', error);
      setCopyStatus('❌ Failed to copy');
      setTimeout(() => setCopyStatus(''), 3000);
    }
  };

  const refreshToken = async () => {
    setIsRefreshing(true);
    setCopyStatus('');
    try {
      console.log('🔄 Refreshing token...');
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        forceRefresh: true, // Force a new token from the server
      });

      console.log('✅ Got new token from MSAL');
      console.log('Token length:', response.accessToken?.length);
      console.log('Token preview:', response.accessToken?.substring(0, 50) + '...');

      // Validate token format before decoding
      if (!response.accessToken || typeof response.accessToken !== 'string') {
        throw new Error('Invalid token received from MSAL');
      }

      const parts = response.accessToken.split('.');
      if (parts.length !== 3) {
        throw new Error(`Invalid JWT format: expected 3 parts, got ${parts.length}`);
      }

      extractTokenInfo(response.accessToken);
      setCopyStatus('✅ Token refreshed!');
      setTimeout(() => setCopyStatus(''), 3000);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      setCopyStatus('❌ Failed to refresh token');
      setTimeout(() => setCopyStatus(''), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!isOpen) return null;

  const infoRow = 'flex gap-2 text-label text-gray-700';
  const sectionLbl = 'block text-meta font-semibold text-gray-500 uppercase tracking-wider mb-1.5';
  const actionBtn = 'px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">🔑 CLI Authentication Token</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg">✕</button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-5">
          {tokenInfo ? (
            <>
              {/* Token info */}
              <div className="bg-surface-subtle rounded-lg p-4 flex flex-col gap-2 border border-gray-200">
                <div className={infoRow}><strong>User:</strong> {tokenInfo.email || 'N/A'}</div>
                {tokenInfo.name && <div className={infoRow}><strong>Name:</strong> {tokenInfo.name}</div>}
                <div className={infoRow}>
                  <strong>Expires:</strong> {tokenInfo.expiresAt.toLocaleString()}{' '}
                  {tokenInfo.isExpired
                    ? <span className="text-error font-medium">⚠️ EXPIRED</span>
                    : <span className="text-success font-medium">✅ ({tokenInfo.minutesLeft} min left)</span>
                  }
                </div>
                {tokenInfo.isExpired && (
                  <button type="button" onClick={refreshToken} disabled={isRefreshing} className={actionBtn}>
                    {isRefreshing ? '🔄 Refreshing…' : '🔄 Get New Token'}
                  </button>
                )}
              </div>

              {/* Token value */}
              <div>
                <span className={sectionLbl}>Your Access Token</span>
                <div className="bg-gray-900 text-green-400 font-mono text-meta p-4 rounded-lg break-all max-h-32 overflow-y-auto border border-gray-700">
                  {tokenInfo.token}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <button type="button" onClick={copyToken} className={actionBtn}>📋 Copy Token</button>
                  {copyStatus && <span className="text-label text-gray-600">{copyStatus}</span>}
                </div>
              </div>

              {/* Usage */}
              <div>
                <span className={sectionLbl}>Usage Example</span>
                <pre className="bg-gray-900 text-gray-300 font-mono text-meta p-4 rounded-lg overflow-x-auto text-left leading-relaxed border border-gray-700">
{`# Navigate to workflow-cli directory
cd d:/Software_Projects/CCStudio/workflow-cli

# Set token (Windows PowerShell)
$env:CCSTUDIO_TOKEN="${tokenInfo.token.substring(0, 30)}..."

# Publish a workflow
node dist/cli.js publish --endpoint http://localhost:7071`}
                </pre>
              </div>

              {/* Warning */}
              <div className="bg-warning-light border border-warning/30 rounded-lg p-4">
                <div className="text-label font-semibold text-warning-dark mb-2">⚠️ Security Warning</div>
                <ul className="list-disc list-inside flex flex-col gap-1">
                  {['This token grants full access to your account', 'Never share this token with anyone', 'Tokens expire after ~1 hour', 'Only use this token for local development'].map(w => (
                    <li key={w} className="text-label text-warning-dark">{w}</li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-body text-error">❌ Failed to extract token information</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-label font-medium rounded-md hover:bg-gray-50 transition-colors duration-fast shadow-xs">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CliTokenModal;

